const STATUS_PRIORITY = { absent: 1, late: 2, none: 3, present: 4 };
import { enrichRecord } from "../../utils/recordUtils";
const KOREAN_NUMERIC_COLLATOR = new Intl.Collator("ko", {
  numeric: true,
  sensitivity: "base",
});

const CLASS_GRADE_WEIGHT = {
  "초1": 1,
  "초2": 2,
  "초3": 3,
  "초4": 4,
  "초5": 5,
  "초6": 6,
  "중1": 7,
  "중2": 8,
  "중3": 9,
  "고1": 10,
  "고2": 11,
  "고3": 12,
};

export const getGradeWeight = (classStr) => {
  if (!classStr) return 999;

  const gradeMatch = classStr.match(/^(중[1-3]|고[1-3]|초[1-6])/);
  if (!gradeMatch) return 999;

  const baseWeight = CLASS_GRADE_WEIGHT[gradeMatch[1]] || 999;
  const suffix = classStr.slice(gradeMatch[1].length);
  const suffixWeight = suffix === "남" ? 0.1 : suffix === "여" ? 0.2 : 0;
  return baseWeight + suffixWeight;
};

const getClassNumericWeight = (classStr) => {
  if (!classStr) return 999;
  const normalized = String(classStr).trim();
  if (!normalized) return 999;

  const gradeWeight = getGradeWeight(normalized);
  const classMatch = normalized.match(/(\d+)\s*반$/);

  if (gradeWeight !== 999 && classMatch) {
    return gradeWeight * 100 + Number(classMatch[1]);
  }

  if (classMatch) return Number(classMatch[1]);

  if (gradeWeight !== 999) return gradeWeight * 100;

  const anyNumberMatch = normalized.match(/(\d+)/);
  if (anyNumberMatch) return Number(anyNumberMatch[1]);

  return 999;
};

const getStudentGradeSortWeight = (student = {}) => {
  const classWeight = getGradeWeight(student.class || "");
  if (classWeight !== 999) return classWeight;
  return getGradeWeight(student.grade || student.department || "");
};

export const mapAttendanceForTargetList = (targetList = [], attendanceData = {}) => {
  const mapped = {};
  targetList.forEach((target) => {
    mapped[target.id] = attendanceData[target.id] || "none";
  });
  return mapped;
};

export const extractAttendanceLogPayload = (log = {}) => {
  const { id, date, ...payload } = log;
  return payload;
};

export const getAttendanceCheckedCount = (attendanceMap = {}) =>
  Object.values(attendanceMap).filter((value) => value === "present" || value === "late").length;

export const getSundaysInMonth = (monthStr) => {
  const [year, month] = monthStr.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  const sundays = [];

  while (date.getDay() !== 0) date.setDate(date.getDate() + 1);
  while (date.getMonth() === month - 1) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    sundays.push(`${y}-${m}-${d}`);
    date.setDate(date.getDate() + 7);
  }

  return sundays;
};

export const getAllSundaysInYear = (yearValue) => {
  const year = Number(yearValue);
  const allSundays = [];

  for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
    const date = new Date(year, monthIndex, 1);
    while (date.getDay() !== 0) date.setDate(date.getDate() + 1);
    while (date.getMonth() === monthIndex) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      allSundays.push(`${y}-${m}-${d}`);
      date.setDate(date.getDate() + 7);
    }
  }

  return allSundays;
};

export const buildFilteredAttendanceTargets = ({
  targetList = [],
  searchQuery = "",
  selectedDepartment = "All",
  targetGroup = "students",
  dailyAttendance = {},
  sortKey = "name",
  sortOrder = "asc",
  matchSearchFn,
  attendanceLogs = [],
  visitLogs = [],
}) => {
  const filtered = targetList.filter((target) => {
    const matchesSearch = matchSearchFn(target.name, searchQuery);
    const matchesDepartment =
      selectedDepartment === "All" ||
      selectedDepartment === "전체" ||
      (targetGroup === "students"
        ? (target.department || target.grade) === selectedDepartment
        : (() => {
          const teacherDepartment = target.department || target.deptName || target.grade || "";
          if (teacherDepartment) {
            return teacherDepartment === selectedDepartment;
          }
          return (target.charge || "").includes(selectedDepartment);
        })());

    return matchesSearch && matchesDepartment;
  });

  // Separate long-term absent members
  const longTermAbsents = filtered.filter(t => t.faithState === "장기결석");
  const activeMembers = filtered.filter(t => t.faithState !== "장기결석");

  activeMembers.sort((a, b) => {
    let comparison = 0;

    if (sortKey === "status") {
      const statusA = dailyAttendance[a.id] || "none";
      const statusB = dailyAttendance[b.id] || "none";
      comparison = (STATUS_PRIORITY[statusA] || 99) - (STATUS_PRIORITY[statusB] || 99);
    } else if (sortKey === "grade") {
      if (targetGroup === "students") {
        const gradeWeightA = getStudentGradeSortWeight(a);
        const gradeWeightB = getStudentGradeSortWeight(b);
        if (gradeWeightA !== gradeWeightB) {
          comparison = gradeWeightA - gradeWeightB;
        } else {
          comparison = KOREAN_NUMERIC_COLLATOR.compare(a.class || "", b.class || "");
        }
      } else {
        comparison = (a.role || "").localeCompare(b.role || "");
      }
    } else if (sortKey === "class") {
      const classWeightA = getClassNumericWeight(a.class || "");
      const classWeightB = getClassNumericWeight(b.class || "");
      if (classWeightA !== classWeightB) {
        comparison = classWeightA - classWeightB;
      } else {
        comparison = KOREAN_NUMERIC_COLLATOR.compare(a.class || "", b.class || "");
      }
    } else if (sortKey === "responsibility") {
      comparison = (a.charge || "").localeCompare(b.charge || "");
    } else if (sortKey === "role") {
      comparison = (a.role || "").localeCompare(b.role || "");
    } else if (sortKey === "department") {
      const departmentA = targetGroup === "students" ? a.grade || "" : a.charge || "";
      const departmentB = targetGroup === "students" ? b.grade || "" : b.charge || "";
      comparison = departmentA.localeCompare(departmentB);
    } else {
      comparison = (a.name || "").localeCompare(b.name || "");
    }

    return sortOrder === "desc" ? -comparison : comparison;
  });

  // Also sort long-term absents by name initially
  longTermAbsents.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // [FIX] 최근 출석/심방 기록 강화 추가
  return [...activeMembers, ...longTermAbsents].map(t => enrichRecord(t, attendanceLogs, visitLogs));
};


export const buildDailyAttendanceStats = (filteredTargets = [], dailyAttendance = {}) => {
  const stats = { present: 0, late: 0, absent: 0, none: 0 };

  filteredTargets.forEach((target) => {
    const status = dailyAttendance[target.id] || "none";
    if (stats[status] !== undefined) stats[status] += 1;
    else stats.none += 1;
  });

  return stats;
};

export const getNextAttendanceStatus = (currentStatus) => {
  const sequence = ["none", "present", "late", "absent"];
  const currentIndex = sequence.indexOf(currentStatus || "none");
  return sequence[(currentIndex + 1) % sequence.length];
};

export const getMonthlyAttendanceRate = (studentId, sundaysInMonth = [], monthlyData = {}) => {
  let presentCount = 0;

  sundaysInMonth.forEach((date) => {
    if (monthlyData[date]?.[studentId] === "present" || monthlyData[date]?.[studentId] === "late") {
      presentCount += 1;
    }
  });

  return sundaysInMonth.length > 0 ? Math.round((presentCount / sundaysInMonth.length) * 100) : 0;
};

export const buildYearlyAttendanceData = ({
  targetList = [],
  currentYear,
  allSundays = [],
  fetchedData = {},
}) => {
  const aggregated = {};

  targetList.forEach((target) => {
    aggregated[target.id] = {};

    for (let month = 1; month <= 12; month += 1) {
      const monthPrefix = `${currentYear}-${String(month).padStart(2, "0")}`;
      const sundaysInMonth = allSundays.filter((date) => date.startsWith(monthPrefix));

      if (sundaysInMonth.length === 0) {
        aggregated[target.id][month] = 0;
        continue;
      }

      let presentCount = 0;
      sundaysInMonth.forEach((day) => {
        const status = fetchedData[day]?.[target.id];
        if (status === "present" || status === "late") {
          presentCount += 1;
        }
      });

      aggregated[target.id][month] = Math.round((presentCount / sundaysInMonth.length) * 100);
    }
  });

  return aggregated;
};

export const buildViewingStudentDetail = ({
  student,
  visitLogs = [],
  attendanceLogs = [],
}) => {
  return enrichRecord(student, attendanceLogs, visitLogs);
};

export const buildSoteriaSyncedAttendance = ({
  attendees = [],
  dailyAttendance = {},
  targetList = [],
}) => {
  let matchCount = 0;
  const syncedAttendance = { ...dailyAttendance };

  attendees.forEach((qrUser) => {
    const normalizedQrName = (qrUser.userName || "").replace(/\s/g, "");
    const targetStudent = targetList.find(
      (student) => (student.name || "").replace(/\s/g, "") === normalizedQrName
    );

    if (!targetStudent) return;

    if (syncedAttendance[targetStudent.id] !== "present") {
      syncedAttendance[targetStudent.id] = "present";
      matchCount += 1;
    }
  });

  return { syncedAttendance, matchCount };
};
